import { computed, ref } from 'vue';
import { useEndpoint } from './useEndpoint';
import { useStringFunctions } from './useStringFunctions';
import { useAiProfileValidation } from './useAiProfileValidation';
import type {
  UpdateSectionRequest,
  Section,
  RootResponse,
  UpdateSectionResponse,
  RegenerateSectionResponse,
  SectionEditCompleteData,
  SectionFormat,
  GenerateWithPromptRequest,
  ErrorResponse,
} from '@apparatix/types/AccountProfiles';
import { useToastNotifications } from '@apparatix/composables';

export const useAccountProfiles = () => {
  const hasGeneratedProfile = ref(false);
  const sections = ref<Section[]>([]);
  const profileId = ref<number>();
  const allowedSectionAttempts = ref<number>();
  const allowedProfileAttempts = ref<number>();
  const profileExecutionCount = ref<number>();
  const isDisclaimerAccepted = ref(false);
  const isGeneratingOnPageOpen = ref(false);
  const isSectionLoadingOnPageOpen = ref(false);
  const isSectionLoading = ref(false);

  const { isEmptyString } = useStringFunctions();
  const { showError } = useToastNotifications();
  const {
    validateBlobSchema,
    validateCompositeListSchema,
    validateListSchema,
    validateTableSchema,
  } = useAiProfileValidation();

  const rootEndpoint = useEndpoint<RootResponse>({
    startInLoadingState: true,
    successField: 'data',
    type: 'GET',
    toast: {
      showOnSuccess: false,
    },
    onSuccess: response => {
      isGeneratingOnPageOpen.value = false;
      isSectionLoadingOnPageOpen.value = false;

      if (response.data && response.data.profile) {
        profileId.value = response.data.profile.id;

        if (response.data.profile.json?.ExecutionCount > 0) {
          hasGeneratedProfile.value = true;
          profileExecutionCount.value = response.data.profile.json.ExecutionCount;
        }
      }
      isDisclaimerAccepted.value = response.data.userAcceptedDisclaimer;

      prepareSections(response.data.sections);

      sections.value = response.data.sections;

      // If we are given a number greater than zero we are loading. check if it's a section, if it's not then the whole
      // profile is generating.
      if (response.data.regeneratingSection > 0) {
        const isSingleSectionLoading = setSectionLoading(true, response.data.regeneratingSection);
        if (! isSingleSectionLoading) {
          // then whole page is loading
          isGeneratingOnPageOpen.value = true;
        } else {
          isSectionLoadingOnPageOpen.value = true;
        }

      }
      allowedSectionAttempts.value = response.data.options.sectionLimit;
      allowedProfileAttempts.value = response.data.options.templateLimit;
    },
  });

  const generateProfileEndpoint = useEndpoint<RootResponse, ErrorResponse>({
    type: 'POST',
    successField: 'data',
    toast: {
      showOnSuccess: false,
    },
    onSuccess: response => {
      hasGeneratedProfile.value = true;
      prepareSections(response.data.sections);
      sections.value = response.data.sections;
      allowedSectionAttempts.value = response.data.options.sectionLimit;
      allowedProfileAttempts.value = response.data.options.templateLimit;

      if (response.data?.profile.json?.ExecutionCount) {
        profileExecutionCount.value = response.data.profile.json.ExecutionCount;
      }
    },
    onError: (err) => {
      hasGeneratedProfile.value = false;
      showError(err?.message ?? '');
    },
  });

  const updateSectionEndpoint = useEndpoint<UpdateSectionResponse>({
    type: 'PUT',
    successField: 'data',
    toast: {
      showOnSuccess: false,
    },
    onSuccess: response => {
      const [section] = response.data.sections;
      prepareSections(section);
      updateSection(section);
      setSectionLoading(false, section.id);
    },
  });

  const regenerateSectionEndpoint = useEndpoint<RegenerateSectionResponse>({
    type: 'POST',
    successField: 'data',
    toast: {
      showOnSuccess: false,
    },
    onSuccess: response => {
      const section = response.data.sections;
      prepareSections(section);
      updateSection(section);
      setSectionLoading(false, section.id);
    },
  });

  const setSectionFormat = (section: Section) => {
    if (section.formatOption) {
      try {
        if (typeof section.formatOption === 'string') {
          const option = JSON.parse(section.formatOption) as { format: SectionFormat, columns?: string[] };
          section.format = option.format;
          section.formatOption = option;
        } else if (typeof section.formatOption === 'object') {
          section.format = section.formatOption.format;
        } else {
          console.log('Invalid section format provided for', section);
        }
      } catch {
        console.log('Unable to parse section format json', section);
      }
    } else {
      console.log('missing format option', section);
    }
  };

  const prepareSections = (_sections: Section[] | Section) => {
    const prepareSection = (section: Section) => {
      setSectionFormat(section);
      section.key = crypto.randomUUID();
      section.isLoading = false;
    };

    if (!Array.isArray(_sections)) {
      prepareSection(_sections);
      return;
    }

    _sections.forEach(section => prepareSection(section));
  };

  const isLoading = computed(() => (rootEndpoint.isLoading.value || generateProfileEndpoint.isLoading.value || isGeneratingOnPageOpen.value) && ! isSectionLoadingOnPageOpen.value);
  const generatingProfile = computed(() => generateProfileEndpoint.isLoading.value || isGeneratingOnPageOpen.value);

  const canRegenerateProfile = computed(() => {
    if (!profileExecutionCount.value || !allowedProfileAttempts.value) return true;
    return profileExecutionCount.value < allowedProfileAttempts.value;
  });

  const loadProfile = async (accountId: number) => {
    const url = `/profiles/accounts/${accountId}`;
    rootEndpoint.setEndpointUrl(url);
    rootEndpoint.makeRequest();
  };

  const generateProfiles = async (accountId: number) => {
    const url = `/profiles/accounts/${accountId}`;
    generateProfileEndpoint.setEndpointUrl(url);
    generateProfileEndpoint.makeRequest();
  };

  const manuallyUpdateSection = async (data: SectionEditCompleteData) => {
    const url = `/profiles/accounts/${data.accountId}/${data.id}`;
    updateSectionEndpoint.setEndpointUrl(url);
    const payload: UpdateSectionRequest = {
      data: {
        contents: data.contents,
        manualEdit: data.source === 'user',
      },
    };

    await updateSectionEndpoint.makeRequest(payload);
  };

  const regenerateSection = async (accountId: number, sectionId: number, userPrompt?: string): Promise<Section> => {
    const url = `/profiles/accounts/${accountId}/${sectionId}`;
    regenerateSectionEndpoint.setEndpointUrl(url);

    const payload: GenerateWithPromptRequest = userPrompt ?
      {
        data: {
          contents: userPrompt,
        },
      }
      : undefined;

    try {
      const response = await regenerateSectionEndpoint.getResults(payload);
      const section = response.data.sections;
      return section;
    } catch {
      return handleResponseError(sectionId);
    }
  };

  const handleResponseError = (sectionId: number) => {
    const section = sections.value.find(s => s.id === sectionId);
    if (!section) {
      return undefined;
    }

    section.format = 'fallback';
    section.isLoading = false;
    return section;
  };

  const textBlobToArray = (input: string): string[] => input?.replace('\r', '')?.split('\n')?.filter(line => !isEmptyString(line, true)) ?? [];

  const updateSection = (section: Section) => {
    const idx = sections.value.findIndex(s => s.id === section.id);
    if (idx === -1) return;
    sections.value[idx] = section;
  };

  const setSectionLoading = (loading: boolean, id: number) => {
    const idx = sections.value.findIndex(section => section.id === id);
    if (idx === -1) return false;
    sections.value[idx].isLoading = loading;
    return true;
  };


  const validateSectionContents = (section: Section, isUnapproved = false) => {
    if (!section?.formatOption?.format) {
      return false;
    }

    switch (section.formatOption.format) {
      case 'blob':
        return validateBlobSchema(isUnapproved ? section.unapprovedContents : section.contents);
      case 'list':
        return validateListSchema(isUnapproved ? section.unapprovedContents : section.contents, section.formatOption.columns);
      case 'table':
        return validateTableSchema(isUnapproved ? section.unapprovedContents : section.contents, section.formatOption.columns);
      case 'composite-list':
        return validateCompositeListSchema(isUnapproved ? section.unapprovedContents : section.contents);
      default:
        return false;
    }
  };

  return {
    allowedProfileAttempts,
    allowedSectionAttempts,
    canRegenerateProfile,
    generateProfiles,
    generatingProfile,
    hasGeneratedProfile,
    isDisclaimerAccepted,
    isGeneratingOnPageOpen,
    isLoading,
    isSectionLoading,
    isSectionLoadingOnPageOpen,
    loadProfile,
    manuallyUpdateSection,
    regenerateSection,
    sections,
    setSectionLoading,
    textBlobToArray,
    updateSection,
    validateSectionContents,
  };
};
